/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import { FlexContainer, Text, theme, Tooltip } from "@nordcloud/gnui";
import { DottedLine } from "~/components/Styled";

type LegendProps = {
  color: string;
  label?: string;
  opacity?: number;
  isDotted?: boolean;
  hasTooltip?: boolean;
  caption?: string;
};

type Props = {
  data: LegendProps[];
  inline?: boolean;
};

export function Legend({ data, inline = false }: Props) {
  const items = data.map((item: LegendProps) => {
    return (
      <FlexContainer
        key={item.label}
        alignItems="center"
        marginRight={inline ? theme.spacing.spacing04 : 0}
      >
        <If condition={item.isDotted}>
          <Then>
            <DottedLine color={theme.colors.statusDanger[1]} />
          </Then>
          <Else>
            <div
              css={{
                opacity: item.opacity ?? 1,
                width: "1rem",
                height: "1rem",
                marginRight: theme.spacing.spacing02,
                borderRadius: theme.radius.md,
                backgroundColor: item.color,
              }}
            />
          </Else>
        </If>
        <If condition={item.hasTooltip}>
          <Then>
            <Tooltip position="left" caption={item.caption}>
              <Text
                css={{ margin: theme.spacing.spacing01 }}
                color={theme.color.text.text01}
                size="sm"
              >
                {item.label}
              </Text>
            </Tooltip>
          </Then>
          <Else>
            <Text
              css={{ margin: theme.spacing.spacing01 }}
              color={theme.color.text.text01}
              size="sm"
            >
              {item.label}
            </Text>
          </Else>
        </If>
      </FlexContainer>
    );
  });

  return (
    <FlexContainer
      direction="column"
      alignItems="flex-start"
      grow={1}
      css={{
        flexDirection: inline ? "row" : undefined,
        flexWrap: inline ? "wrap" : undefined,
      }}
    >
      {items}
    </FlexContainer>
  );
}
